import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'

import { ApiError } from '@/api/client'
import type { TwoFactorSetup } from '@/api/endpoints'
import { authApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { User } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { CopyField } from '@/features/access'
import { Field } from '@/features/auth'
import { useI18n, useT } from '@/i18n'
import { useFormat } from '@/lib/format'

import { SettingsSection as Section } from './SettingsSection'

type Step =
  | { kind: 'idle' }
  | { kind: 'password'; purpose: 'enable' | 'regenerate' | 'disable' }
  | { kind: 'scan'; setup: TwoFactorSetup }
  | { kind: 'codes'; codes: string[] }

/**
 * 两步验证（D88–D90）。
 *
 * 开启：输密码 → 扫码（或手动输入密钥）→ 输一次验证码确认 → 看到恢复码（只这一次）。
 * 已开启：显示还剩几个恢复码；重新生成与关闭都要再输一次密码。
 */
export function TwoFactorSection() {
  const { t, tp } = useI18n()
  const toast = useToast()
  const client = useQueryClient()
  const { absoluteTime } = useFormat()
  const status = useQuery({ queryKey: keys.twoFactor(), queryFn: authApi.twoFactorStatus })
  const [step, setStep] = useState<Step>({ kind: 'idle' })

  const markEnabled = (enabled: boolean) => {
    client.setQueryData<User>(keys.me(), (me) => (me ? { ...me, two_factor_enabled: enabled } : me))
    client.invalidateQueries({ queryKey: keys.twoFactor() })
  }

  let body: ReactNode
  if (step.kind === 'password') {
    body = (
      <PasswordStep
        purpose={step.purpose}
        onCancel={() => setStep({ kind: 'idle' })}
        onSetup={(setup) => setStep({ kind: 'scan', setup })}
        onCodes={(codes) => {
          client.invalidateQueries({ queryKey: keys.twoFactor() })
          setStep({ kind: 'codes', codes })
        }}
        onDisabled={() => {
          markEnabled(false)
          setStep({ kind: 'idle' })
          toast.show(t('twoFactor.disabled'))
        }}
      />
    )
  } else if (step.kind === 'scan') {
    body = (
      <ScanStep
        setup={step.setup}
        onCancel={() => setStep({ kind: 'idle' })}
        onEnabled={(codes, revoked) => {
          markEnabled(true)
          client.invalidateQueries({ queryKey: keys.sessions() })
          setStep({ kind: 'codes', codes })
          toast.show(
            revoked > 0 ? tp('twoFactor.enabledWithSessions', revoked) : t('twoFactor.enabled'),
          )
        }}
      />
    )
  } else if (step.kind === 'codes') {
    body = <RecoveryCodes codes={step.codes} onDone={() => setStep({ kind: 'idle' })} />
  } else if (status.data?.enabled) {
    const left = status.data.recovery_codes_left
    body = (
      <div className="space-y-3">
        <p className="text-[15px]">
          {t('twoFactor.on', {
            date: status.data.enabled_at ? absoluteTime(status.data.enabled_at) : '',
          })}
        </p>
        <p className={left <= 3 ? 'text-[15px] text-danger' : 'text-[15px] text-ink-muted'}>
          {tp('twoFactor.codesLeft', left)}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setStep({ kind: 'password', purpose: 'regenerate' })}
          >
            {t('twoFactor.regenerate')}
          </Button>
          <Button
            variant="ghost"
            className="text-danger"
            onClick={() => setStep({ kind: 'password', purpose: 'disable' })}
          >
            {t('twoFactor.turnOff')}
          </Button>
        </div>
      </div>
    )
  } else if (status.data) {
    body = (
      <Button onClick={() => setStep({ kind: 'password', purpose: 'enable' })}>
        {t('twoFactor.turnOn')}
      </Button>
    )
  } else {
    body = null
  }

  return (
    <Section title={t('twoFactor.title')} description={t('twoFactor.description')}>
      {body}
    </Section>
  )
}

function PasswordStep({
  purpose,
  onCancel,
  onSetup,
  onCodes,
  onDisabled,
}: {
  purpose: 'enable' | 'regenerate' | 'disable'
  onCancel: () => void
  onSetup: (setup: TwoFactorSetup) => void
  onCodes: (codes: string[]) => void
  onDisabled: () => void
}) {
  const t = useT()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const submit = useMutation({
    mutationFn: async () => {
      if (purpose === 'enable') onSetup(await authApi.twoFactorSetup(password))
      else if (purpose === 'regenerate')
        onCodes((await authApi.regenerateRecoveryCodes(password)).recovery_codes)
      else {
        await authApi.twoFactorDisable(password, code.trim())
        onDisabled()
      }
    },
  })
  const error = submit.error instanceof ApiError ? submit.error : null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    submit.mutate()
  }

  return (
    <form onSubmit={onSubmit} className="max-w-sm space-y-3" noValidate>
      <Field
        label={t('account.currentPassword')}
        name="two_factor_password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
        required
      />
      {purpose === 'disable' && (
        <Field
          label={t('twoFactor.codeOrRecovery')}
          name="two_factor_code"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error.code === 'wrong_password'
            ? t('twoFactor.wrongPassword')
            : error.code === 'invalid_code'
              ? t('twoFactor.wrongCode')
              : error.message}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" loading={submit.isPending} disabled={!password}>
          {purpose === 'disable' ? t('twoFactor.turnOff') : t('twoFactor.continue')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function ScanStep({
  setup,
  onCancel,
  onEnabled,
}: {
  setup: TwoFactorSetup
  onCancel: () => void
  onEnabled: (codes: string[], revoked: number) => void
}) {
  const t = useT()
  const [code, setCode] = useState('')
  const enable = useMutation({
    mutationFn: () => authApi.twoFactorEnable(code.trim()),
    onSuccess: (result) => onEnabled(result.recovery_codes, result.revoked_sessions),
  })
  const error = enable.error instanceof ApiError ? enable.error : null
  // 手动输入时四个一组好抄
  const grouped = setup.secret.replace(/(.{4})/g, '$1 ').trim()

  return (
    <div className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-[15px] text-ink-muted">
        <li>{t('twoFactor.scanStep1')}</li>
        <li>{t('twoFactor.scanStep2')}</li>
      </ol>
      <div className="flex flex-wrap items-start gap-4">
        {/* 二维码白底黑码：深色模式下也要能被扫出来 */}
        <img
          src={setup.qr_svg}
          alt={t('twoFactor.qrAlt')}
          className="size-44 shrink-0 rounded-md bg-white p-1"
        />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-[13px] text-ink-muted">{t('twoFactor.manualKey')}</p>
          <CopyField value={grouped} label={t('twoFactor.manualKeyLabel')} />
          <a href={setup.otpauth_url} className="text-[13px] text-brand-ink hover:underline">
            {t('twoFactor.openInApp')}
          </a>
        </div>
      </div>
      <form
        className="max-w-sm space-y-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          enable.mutate()
        }}
      >
        <Field
          label={t('twoFactor.code')}
          name="two_factor_setup_code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error.code === 'invalid_code' ? t('twoFactor.wrongCode') : error.message}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="submit" loading={enable.isPending} disabled={code.trim().length < 6}>
            {t('twoFactor.confirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const t = useT()
  const text = codes.join('\n')
  const download = () => {
    const url = URL.createObjectURL(new Blob([`PlazaPlus\n\n${text}\n`], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'plazaplus-recovery-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      <p role="status" className="text-[15px] font-bold">
        {t('twoFactor.codesTitle')}
      </p>
      <p className="text-[15px] text-ink-muted">{t('twoFactor.codesBody')}</p>
      <ul
        aria-label={t('twoFactor.codesTitle')}
        className="grid max-w-sm grid-cols-2 gap-x-6 gap-y-1 rounded-md bg-surface-subtle p-3 font-mono text-[15px]"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => void navigator.clipboard?.writeText(text)}>
          {t('twoFactor.copyCodes')}
        </Button>
        <Button variant="outline" onClick={download}>
          {t('twoFactor.downloadCodes')}
        </Button>
        <Button onClick={onDone}>{t('twoFactor.savedThem')}</Button>
      </div>
    </div>
  )
}
